import { EnvelopeIcon, StarIcon, UserIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { signIn, useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { isAPIError } from '@/api/axiosHelper';
import { login } from '@/api/user/login';
import { passwordReset } from '@/api/user/passwordReset';
import { registerGuest as registerAxios } from '@/api/user/registerGuest';
import { Translation } from '@/types/translation';
import { generateRandomPassword } from '@/utils/generateRandomPassword';
import { generateUniqueEmail } from '@/utils/generateUniqueEmail';
import { EMAIL_REGEX } from '@/utils/regex';

import { ForgotPassword, Login } from '../Form/Auth/Authform.type';
import { TextField } from '../Form/UI/TextField';
import Button from '../UI/Button';
import Modal from './Modal';

export type LoginModalActualForm = 'login' | 'verify';

interface LoginModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  email: string;
  firstname?: string;
  lastname?: string;
  onSuccessCallback: () => void;
  defaultForm?: LoginModalActualForm;
  bypassResetEmailSending: boolean;
  allowGuestAccount?: boolean;
}

export default function LoginModal({
  open,
  setOpen,
  email,
  firstname,
  lastname,
  onSuccessCallback,
  defaultForm = 'login',
  bypassResetEmailSending,
  allowGuestAccount = false,
}: LoginModalProps) {
  const t = useTranslations('Components.Modal.LoginModal');
  const tErrors = useTranslations('Errors.Form');
  const [actualForm, setActualForm] =
    useState<LoginModalActualForm>(defaultForm);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      closeButton={false}
      panelClassName='backdrop-filter backdrop-blur-lg bg-[#101114]/[90]'
      padding='px-4 mb-20 sm:px-8'
    >
      {actualForm === 'login' && (
        <LoginForm
          t={t}
          tErrors={tErrors}
          email={email}
          firstname={firstname}
          lastname={lastname}
          allowGuestAccount={allowGuestAccount}
          onSuccessCallback={onSuccessCallback}
          setActualForm={setActualForm}
        />
      )}
      {actualForm === 'verify' && (
        <VerifyAccount
          t={t}
          tErrors={tErrors}
          email={email}
          setActualForm={setActualForm}
          emailSendAutomatically={bypassResetEmailSending}
        />
      )}
    </Modal>
  );
}

interface VerifyAccountProps {
  tErrors: Translation;
  t: Translation;
  setActualForm: Dispatch<SetStateAction<LoginModalActualForm>>;
  email: string;
  emailSendAutomatically: boolean;
}

const VerifyAccount = ({
  tErrors,
  t,
  setActualForm,
  email,
  emailSendAutomatically,
}: VerifyAccountProps) => {
  const [isEmailSent, setIsEmailSent] = useState(emailSendAutomatically);

  const passwordResetMutation = useMutation(passwordReset);
  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = useForm<ForgotPassword>({ mode: 'all', defaultValues: { email } });

  const emailWatcher = watch('email');

  const onSubmit = async (formdata: ForgotPassword) => {
    await passwordResetMutation.mutateAsync(
      { email: formdata.email },
      {
        onSuccess() {
          setIsEmailSent(true);
        },
        onError() {
          toast.error(t('errorOccured'));
          setIsEmailSent(true);
        },
      }
    );
  };

  const backToLogin = () => setActualForm('login');
  return (
    <div className='relative'>
      <div className='pt-10'>
        <div className='isolate mx-auto flex size-14 items-center justify-center rounded-full bg-primary-filled-default'>
          <div className='absolute size-11 animate-ping-slow rounded-full bg-primary-filled-default' />
          <div className='time absolute size-14 animate-ping-slow rounded-full bg-primary-filled-default' />
          <div className='absolute size-64 rounded-full bg-primary-filled-default/[0.12] blur-2xl' />
          <EnvelopeIcon className='relative z-10 h-6 w-6' />
        </div>
      </div>
      {!isEmailSent && (
        <form
          className='mb-5 space-y-4 pt-10'
          onSubmit={handleSubmit(onSubmit)}
        >
          <p className='pb-5 text-center text-lg font-bold'>
            {t('verifyYourAccount')}
          </p>
          <TextField
            {...register('email', {
              required: true,
              pattern: {
                value: EMAIL_REGEX,
                message: tErrors('invalidEmail'),
              },
            })}
            label={t('email')}
            placeholder=''
            error={errors.email}
          />
          <Button
            width='full'
            disabled={
              !isValid ||
              passwordResetMutation.isLoading ||
              passwordResetMutation.isSuccess
            }
          >
            {t('verifyAccount')}
          </Button>
        </form>
      )}
      {isEmailSent && (
        <div className='mb-5 space-y-5 pt-10 text-center'>
          <p>
            {(t as any).rich('emailSent', {
              email: emailWatcher,
              span: (e: string) => <span className='font-bold'>{e}</span>,
            })}
          </p>
          <Button onClick={backToLogin} width='full'>
            {t('accountVerified')}
          </Button>
        </div>
      )}
    </div>
  );
};

interface LoginProps {
  tErrors: Translation;
  t: Translation;
  email?: string;
  firstname?: string;
  lastname?: string;
  allowGuestAccount?: boolean;
  onSuccessCallback: () => void;
  setActualForm: Dispatch<SetStateAction<LoginModalActualForm>>;
}

const LoginForm = ({
  tErrors,
  t,
  email,
  firstname,
  lastname,
  allowGuestAccount,
  onSuccessCallback,
  setActualForm,
}: LoginProps) => {
  const { status } = useSession();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { isValid, errors },
  } = useForm<Login>({
    mode: 'onBlur',
    defaultValues: { username: email, firstname, lastname },
  });

  const onSubmit = async (formdata: Login) => {
    setIsLoading(true);
    signIn('credentials', {
      type: 'login',
      redirect: false,
      username: formdata.username,
      password: formdata.password,
    })
      .then(async (response) => {
        if (response?.error) {
          const error = JSON.parse(response.error);
          if (error) {
            toast.error(error.message);
          } else {
            toast.error(tErrors('invalid_user_credentials'));
          }
        } else {
          onSuccessCallback?.();
        }
      })
      .catch(() => {
        toast.error(tErrors('errorOccured'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleContinueAsAGuest = () => {
    setIsLoading(true);
    const formdata = getValues();
    const randomGuestPassword = generateRandomPassword();
    const uniqueEmail = generateUniqueEmail(formdata.username as string);

    signIn('credentials', {
      id: 'register',
      type: 'register',
      redirect: false,
      email: uniqueEmail,
      password: randomGuestPassword,
      firstname: formdata.firstname,
      lastname: formdata.lastname,
    })
      .then(async (response) => {
        if (response?.error) {
          const error = JSON.parse(response.error);
          if (error) {
            toast.error(error.message);
          } else {
            toast.error(tErrors('invalid_user_credentials'));
          }
        } else {
          onSuccessCallback?.();
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className='relative'>
      <div className='pt-10'>
        <div className='mx-auto flex size-14 items-center justify-center overflow-hidden rounded-full bg-primary-filled-default'>
          <div className='absolute size-11 animate-ping-slow rounded-full bg-primary-filled-default' />
          <div className='absolute size-14 animate-ping-slow rounded-full bg-primary-filled-default' />
          <div className='absolute size-64 rounded-full bg-primary-filled-default bg-primary-filled-default/[0.12] blur-2xl' />
          <UserIcon className='relative z-10 h-6 w-6' />
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4 pb-5 pt-10'>
        <div className='space-y-2'>
          <p className='text-center text-lg font-bold'>{t('welcomeBack')}</p>
          <p className='pb-5 text-center'>{t('description')}</p>
        </div>

        {allowGuestAccount && (
          <div>
            <div className='flex justify-center sm:mx-auto sm:max-w-md'>
              <Button
                onClick={handleContinueAsAGuest}
                width='full'
                padding='xl'
                disabled={status === 'loading' || isLoading}
                type='button'
                isLoading={isLoading}
              >
                {t('guest')}
              </Button>
            </div>
            <p className='mt-1 text-center text-xs text-white-filled-default'>
              {t('cantReconnectToGuest')}
            </p>
          </div>
        )}
        {allowGuestAccount && (
          <div className='flex items-center'>
            <p className='h-[1px] w-full bg-white-filled-default'></p>
            <p className='px-2 '>{t('or')}</p>
            <p className='h-[1px] w-full bg-white-filled-default'></p>
          </div>
        )}

        <div className='space-y-4'>
          <TextField
            {...register('username', {
              required: true,
              pattern: {
                value: EMAIL_REGEX,
                message: tErrors('invalidEmail'),
              },
            })}
            label={t('email')}
            placeholder=''
            error={errors.username}
          />

          <TextField
            {...register('password', {
              required: true,
            })}
            type='password'
            label={t('password')}
            placeholder=''
          />
        </div>
        <div className='flex flex-row-reverse'>
          <div className='max-w-max'>
            <Button
              variant='link'
              onClick={() => {
                document.body.scrollIntoView({ behavior: 'smooth' });
                setActualForm('verify');
              }}
              width='full'
              type='button'
            >
              <span className='font-medium'>{t('forgotPassword')}</span>
            </Button>
          </div>
        </div>
        <div className='flex justify-center pt-3 sm:mx-auto sm:max-w-md'>
          <Button
            width='full'
            padding='xl'
            disabled={!isValid || isLoading || status === 'loading'}
            type='submit'
            isLoading={isLoading}
          >
            {t('connect')}
          </Button>
        </div>
      </form>
    </div>
  );
};
